import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  DestinationPicker,
  EMPTY_DESTINATION,
  isDestinationReady,
  type Destination,
} from '@/features/destination/DestinationPicker';
import type { SelectedRef } from './selection';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  hint: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground3 },
});

/**
 * « Déplacer N éléments vers… » — la destination d'un déplacement de LOT.
 *
 * Réutilise le sélecteur de destination du rangement plutôt qu'un arbre de plus :
 * la question est la même (quel espace, quel dossier), et deux sélecteurs
 * divergeraient — l'un saurait chercher dans les chemins, l'autre pas.
 *
 * Le dialogue ne déplace RIEN lui-même : il rend la destination choisie. Le lot,
 * ses issues partielles et son compte rendu appartiennent à l'appelant.
 */
export function MoveManyDialog({
  items,
  onClose,
  onConfirm,
  pending,
}: {
  items: SelectedRef[];
  onClose: () => void;
  onConfirm: (dest: { folderId: string | null; containerId: string }) => void;
  pending: boolean;
}) {
  const styles = useStyles();
  const [dest, setDest] = useState<Destination>(EMPTY_DESTINATION);

  const mails = items.filter((i) => i.kind === 'mail').length;
  const folders = items.length - mails;
  const ready = isDestinationReady(dest);
  // Un mail ne se déplace pas à la racine d'un espace : `outlook-move-mail` exige
  // un dossier. On le dit AVANT le clic plutôt que de le compter en « sans effet »
  // après coup, sur une action que l'utilisateur croyait complète.
  const mailsAtRoot = dest.atRoot && mails > 0;

  return (
    <Dialog open onOpenChange={(_e, d) => !d.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            Déplacer {items.length} élément{items.length > 1 ? 's' : ''}
          </DialogTitle>
          <DialogContent className={styles.body}>
            <Text className={styles.hint}>
              {[
                mails > 0 ? `${mails} mail${mails > 1 ? 's' : ''}` : null,
                folders > 0 ? `${folders} dossier${folders > 1 ? 's' : ''}` : null,
              ]
                .filter(Boolean)
                .join(' et ')}
              {folders > 0 ? ', contenu compris.' : '.'}
            </Text>

            <DestinationPicker value={dest} onChange={setDest} />

            {mailsAtRoot && (
              <MessageBar intent="warning">
                <MessageBarBody>
                  Les {mails > 1 ? `${mails} mails` : 'mails'} ne peuvent pas être déplacés à la
                  racine d’un espace, seuls les dossiers le peuvent. Choisissez un dossier, ou
                  déplacez les dossiers seuls.
                </MessageBarBody>
              </MessageBar>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={pending}>
              Annuler
            </Button>
            <Button
              appearance="primary"
              disabled={!ready || pending || mailsAtRoot}
              onClick={() =>
                dest.containerId &&
                onConfirm({ folderId: dest.folderId, containerId: dest.containerId })
              }
            >
              {pending ? 'Déplacement…' : 'Déplacer'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
